import {
  ACTION_ARGS,
  COMPONENT_STATE_ARGS,
  INPUT_MENU_ARGS,
  INPUT_SELECT_ALL_ARGS,
  MENU_ARGS,
  Metadata,
  pick,
} from '../../../../../utils/spds/metadata';
import { HIDDEN_SELECTOR } from '../../../../../utils/keyboard-nav-support';
import { ensureSafeComponent } from '@embroider/util';
import { fn, hash } from '@ember/helper';
import { gte } from 'ember-truth-helpers';
import { reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import CheckboxGroup from '../../group/checkbox.gjs';
import Menu from '../../../core/menu/base.gjs';
import Super from '../../../core/base.gjs';

const SELECT_ALL_LABEL_DEFAULT = 'Select all';
const SELECT_ALL_MIN_DEFAULT = 6;

const METADATA = Metadata.define({
  description: 'Multi-select menu for dropdown lists with checkbox options and select-all support',
  extends: [
    pick(MENU_ARGS, ['anchorElement']),
    pick(ACTION_ARGS, [{ name: 'onClick', required: true }]),
    pick(COMPONENT_STATE_ARGS, [{ name: 'open', required: true }]),
    pick(INPUT_MENU_ARGS, ['noResultsText', 'searchable', 'searchPlaceholder', 'searchThreshold']),
    INPUT_SELECT_ALL_ARGS,
  ],
  name: 'Dropdown List Multiselect Menu',
});

export default class SpdsInputDropdownListMultiselectMenu extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  additionalClasses = 'spds-input-dropdown-list-item';
  baseClass = 'spds-input-dropdown-list-multiselect-menu';

  @tracked checkboxGroup;

  @reads('args.selectAllLabel', SELECT_ALL_LABEL_DEFAULT) selectAllLabel;
  @reads('args.selectAllMin', SELECT_ALL_MIN_DEFAULT) selectAllThreshold;
  @reads('checkboxGroup.selectAllComponent') selectAllComponent;
  @reads('checkboxGroup.navigableOptions', []) navigableOptions;

  get menuId() {
    return this.composeSubcomponentId('multi-menu');
  }

  <template>
    <Menu
      @anchorElement={{@anchorElement}}
      @animated={{true}}
      @id={{this.id}}
      @open={{@open}}
      @multiselect={{true}}
      @noResultsText={{@noResultsText}}
      @searchable={{@searchable}}
      @searchPlaceholder={{@searchPlaceholder}}
      @searchThreshold={{@searchThreshold}}
      class={{this.classes}}
      data-test-spds-input-dropdown-list-multiselect-menu
      {{this.validateArguments}}
      ...attributes
    >
      <CheckboxGroup
        @groupName={{this.menuId}}
        @hideDescription={{true}}
        @id={{this.menuId}}
        @layout="column"
        @onClick={{@onClick}}
        @onSetup={{@onClick}}
        @registerComponent={{fn this.registerComponent "checkboxGroup"}}
        @unregisterComponent={{fn this.unregisterComponent "checkboxGroup"}}
        as |cg|
      >
        <cg.selectAll
          @inputClasses="{{this.additionalClasses}} select-all"
          @label={{this.selectAllLabel}}
          class={{unless (gte cg.optionCount this.selectAllThreshold) HIDDEN_SELECTOR}}
          data-test-spds-input-dropdown-list-multiselect-menu-select-all-option
        />
        {{yield
          (hash
            item=(component (ensureSafeComponent cg.checkbox) inputClasses=this.additionalClasses)
          )
        }}
      </CheckboxGroup>
    </Menu>
  </template>
}

export const DROPDOWN_LIST_MULTISELECT_MENU_METADATA =
  SpdsInputDropdownListMultiselectMenu.metadata;
